import { useCallback, useEffect, useRef, useState } from "react";
import GoogleConnect from "./components/GoogleConnect";
import GroupsSidebar from "./components/GroupsSidebar";
import PhotosPanel from "./components/PhotosPanel";
import PlacesPanel from "./components/PlacesPanel";
import PublishPage from "./components/PublishPage";
import ReplyTemplates from "./components/ReplyTemplates";
import ReviewsPanel from "./components/ReviewsPanel";
import StatsPanel from "./components/StatsPanel";
import Topbar from "./components/Topbar";
import WorkspaceNav, { type Tab } from "./components/WorkspaceNav";
import { apiGet, apiPost, apiPostForm } from "./api";
import PlaceSwitcher from "./components/PlaceSwitcher";
import { humanError } from "./lib/errors";
import { isOAuthReturn, notifyOAuthOpener, openOAuthUrl } from "./lib/oauth-window";
import type {
  Place,
  PlaceAction,
  PlaceAttr,
  PlaceGroup,
  PlaceHoursPeriod,
  PlacePhoto,
  PlaceStat,
  PresenceMe,
  ReplyTemplate,
  Review,
  SpecialHour,
} from "./types";

export default function App() {
  const [me, setMe] = useState<PresenceMe | null>(null);
  const [places, setPlaces] = useState<Place[]>([]);
  const [groups, setGroups] = useState<PlaceGroup[]>([]);
  const [groupFilter, setGroupFilter] = useState("all");
  const [groupBusy, setGroupBusy] = useState(false);
  const [selected, setSelected] = useState("");
  const [reviewAll, setReviewAll] = useState(false);
  const [draft, setDraft] = useState<PlaceHoursPeriod[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [templates, setTemplates] = useState<ReplyTemplate[]>([]);
  const [templatesBusy, setTemplatesBusy] = useState(false);
  const [tab, setTab] = useState<Tab>("places");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [hoursBusy, setHoursBusy] = useState(false);
  const [replyBusy, setReplyBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState(false);
  const [special, setSpecial] = useState<SpecialHour[]>([]);
  const [specialBusy, setSpecialBusy] = useState(false);
  const [openBusy, setOpenBusy] = useState(false);
  const [attrs, setAttrs] = useState<PlaceAttr[]>([]);
  const [attrsBusy, setAttrsBusy] = useState(false);
  const [actions, setActions] = useState<PlaceAction[]>([]);
  const [actionTypes, setActionTypes] = useState<Record<string, string>>({
    APPOINTMENT: "Prendre rendez-vous",
    FOOD_ORDERING: "Commander",
    SHOP_ONLINE: "Acheter en ligne",
  });
  const [actionsBusy, setActionsBusy] = useState(false);
  const [photos, setPhotos] = useState<PlacePhoto[]>([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [stats, setStats] = useState<PlaceStat[]>([]);

  const loadMe = useCallback(async () => {
    const data = await apiGet<PresenceMe>("me.php");
    setMe(data);
    return data;
  }, []);

  const loadPlaces = useCallback(async () => {
    const data = await apiGet<{ places: Place[]; preview?: boolean }>("google-locations.php");
    const list = data.places || [];
    setPreview(Boolean(data.preview || data.places?.[0]?.name?.startsWith("accounts/preview/")));
    setPlaces(list);
    setSelected((cur) => {
      const next = list.find((p) => p.name === cur) || list[0];
      return next?.name || "";
    });
    return list;
  }, []);

  const loadGroups = useCallback(async () => {
    const data = await apiGet<{ groups: PlaceGroup[] }>("google-groups.php");
    setGroups(data.groups || []);
  }, []);

  const loadPlaceExtra = useCallback(async (name: string) => {
    if (!name) return;
    const q = encodeURIComponent(name);
    const [attrRes, actRes, photoRes, statRes] = await Promise.allSettled([
      apiGet<{ attributes: PlaceAttr[] }>(`google-attributes.php?location=${q}`),
      apiGet<{ actions: PlaceAction[]; types?: Record<string, string> }>(`google-actions.php?location=${q}`),
      apiGet<{ photos: PlacePhoto[] }>(`google-photos.php?location=${q}`),
      apiGet<{ stats: PlaceStat[] }>(`google-stats.php?location=${q}`),
    ]);
    if (attrRes.status === "fulfilled") setAttrs(attrRes.value.attributes || []);
    if (actRes.status === "fulfilled") {
      setActions(actRes.value.actions || []);
      if (actRes.value.types) setActionTypes(actRes.value.types);
    }
    if (photoRes.status === "fulfilled") setPhotos(photoRes.value.photos || []);
    if (statRes.status === "fulfilled") setStats(statRes.value.stats || []);
  }, []);

  const loadReviews = useCallback(async (name: string, list: Place[] = []) => {
    if (!name) {
      setReviews([]);
      return;
    }
    const q = encodeURIComponent(name);
    const data = await apiGet<{ reviews: Review[] }>(`google-reviews.php?location=${q}`);
    const catalog = list.length ? list : places;
    setReviews(
      (data.reviews || []).map((r) => ({
        ...r,
        place_title: r.place_title || catalog.find((p) => p.name === (r.place_name || name))?.title || "",
      })),
    );
  }, [places]);

  const loadTemplates = useCallback(async () => {
    const data = await apiGet<{ templates: ReplyTemplate[] }>("google-reply-templates.php");
    setTemplates(data.templates || []);
  }, []);

  const bootLock = useRef(false);
  const boot = useCallback(async () => {
    if (bootLock.current) return;
    bootLock.current = true;
    try {
      const data = await loadMe();
      setPreview(Boolean(data.google.preview));
      if (data.google.connected) {
        const list = await loadPlaces();
        await loadGroups();
        await loadTemplates();
        const first = list[0]?.name || "";
        if (first) {
          setReviewAll(false);
          await loadPlaceExtra(first);
        }
      }
    } catch (err) {
      const raw = err instanceof Error ? err.message : "";
      if (raw === "session_required" || raw === "unauthorized" || raw === "session_or_userid_required") return;
      setError(humanError(err));
    } finally {
      bootLock.current = false;
    }
  }, [loadMe, loadPlaces, loadGroups, loadTemplates, loadPlaceExtra]);

  const bootRef = useRef(boot);
  bootRef.current = boot;

  useEffect(() => {
    const onOAuth = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isOAuthReturn(event.data)) return;
      const data = event.data;
      if (data.google === "1") setNotice("Google est connecté.");
      if (data.google === "denied") setError(humanError("google_denied"));
      if (data.google === "error") setError(humanError(data.reason || "google_token_failed"));
      if (data.facebook === "1") {
        const n = Number(data.pages || "0");
        setNotice(n > 1 ? `${n} Pages Facebook reliées.` : "Page Facebook reliée.");
        setTab("publish");
      }
      if (data.facebook === "denied") setError(humanError("meta_denied"));
      if (data.facebook === "empty") setError(humanError("meta_no_page"));
      if (data.facebook === "error") setError(humanError(data.reason || "meta_token_failed"));
      if (data.facebook) setTab("publish");
      void bootRef.current();
    };
    window.addEventListener("message", onOAuth);
    return () => window.removeEventListener("message", onOAuth);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    notifyOAuthOpener(params);
    if (params.get("google") === "1") setNotice("Google est connecté.");
    if (params.get("google") === "denied") setError(humanError("google_denied"));
    if (params.get("google") === "error") setError(humanError(params.get("reason") || "google_token_failed"));
    if (params.has("google")) {
      const url = new URL(window.location.href);
      url.searchParams.delete("google");
      url.searchParams.delete("reason");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    if (params.get("facebook") === "1") {
      const n = Number(params.get("pages") || "0");
      setNotice(n > 1 ? `${n} Pages Facebook reliées.` : "Page Facebook reliée.");
      setTab("publish");
    }
    if (params.get("facebook") === "denied") setError(humanError("meta_denied"));
    if (params.get("facebook") === "empty") setError(humanError("meta_no_page"));
    if (params.get("facebook") === "error") setError(humanError(params.get("reason") || "meta_token_failed"));
    if (params.has("facebook")) {
      const url = new URL(window.location.href);
      url.searchParams.delete("facebook");
      url.searchParams.delete("pages");
      url.searchParams.delete("reason");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      setTab("publish");
    }

    const run = () => {
      void bootRef.current();
    };
    if (window.__presenceSessionVerified) run();
    window.addEventListener("presenceAuthReady", run);
    return () => window.removeEventListener("presenceAuthReady", run);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(""), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => setError(""), 6000);
    return () => window.clearTimeout(id);
  }, [error]);

  useEffect(() => {
    const place = places.find((p) => p.name === selected);
    setDraft(place ? place.hours.map((h) => ({ ...h })) : []);
    setSpecial(place ? (place.special_hours || []).map((h) => ({ ...h })) : []);
  }, [places, selected]);

  useEffect(() => {
    if (tab !== "reviews" || !selected || reviewAll) return;
    void loadReviews(selected).catch((err) => setError(humanError(err)));
  }, [tab, selected, reviewAll, loadReviews]);

  async function connectGoogle() {
    setBusy(true);
    setError("");
    try {
      const data = await apiGet<{ url: string }>("google-connect.php");
      if (!data.url) throw new Error("server");
      openOAuthUrl(data.url);
      setBusy(false);
    } catch (err) {
      setError(humanError(err));
      setBusy(false);
    }
  }

  async function disconnectGoogle() {
    setBusy(true);
    setError("");
    try {
      await apiPost<{ ok: boolean }>("google-disconnect.php", {});
      setPlaces([]);
      setSelected("");
      await loadMe();
      setNotice("Compte Google déconnecté.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setBusy(false);
    }
  }

  async function selectPlace(name: string) {
    setSelected(name);
    setReviewAll(false);
    setError("");
    try {
      await loadPlaceExtra(name);
      if (tab === "reviews") await loadReviews(name);
    } catch (err) {
      setError(humanError(err));
    }
  }

  async function pickScope(name: string) {
    if (name === "all") {
      setReviewAll(true);
      setError("");
      try {
        await loadReviews("all");
      } catch (err) {
        setError(humanError(err));
      }
      return;
    }
    await selectPlace(name);
  }

  function patchPlace(name: string, patch: Partial<Place>) {
    setPlaces((list) => list.map((place) => (place.name === name ? { ...place, ...patch } : place)));
  }

  async function saveHours() {
    if (!selected) return;
    setHoursBusy(true);
    setError("");
    const saved = draft.map((row) => ({ ...row }));
    try {
      await apiPost<{ ok: boolean }>("google-hours.php", {
        location: selected,
        hours: saved,
      });
      patchPlace(selected, { hours: saved });
      setNotice(preview ? "Horaires enregistrés." : "Horaires enregistrés sur Google.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setHoursBusy(false);
    }
  }

  async function saveTemplate(tpl: { id?: string; name: string; text: string }) {
    setTemplatesBusy(true);
    setError("");
    try {
      const data = await apiPost<{ templates: ReplyTemplate[] }>("google-reply-templates.php", tpl);
      setTemplates(data.templates || []);
      setNotice("Modèle enregistré.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setTemplatesBusy(false);
    }
  }

  async function deleteTemplate(id: string) {
    setError("");
    try {
      const data = await apiPost<{ templates: ReplyTemplate[] }>("google-reply-templates.php", { delete: id });
      setTemplates(data.templates || []);
    } catch (err) {
      setError(humanError(err));
    }
  }

  async function replyReview(name: string, comment: string) {
    setReplyBusy(name);
    setError("");
    try {
      await apiPost<{ ok: boolean }>("google-review-reply.php", {
        review: name,
        comment,
      });
      await loadReviews(selected);
      setNotice(preview ? "Réponse enregistrée." : "Réponse publiée.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setReplyBusy(null);
    }
  }

  async function saveOpen(status: string) {
    if (!selected) return;
    setOpenBusy(true);
    setError("");
    try {
      await apiPost<{ ok: boolean }>("google-open.php", { location: selected, status });
      patchPlace(selected, { open_status: status as Place["open_status"] });
      setNotice("Ouverture enregistrée.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setOpenBusy(false);
    }
  }

  async function saveSpecial() {
    if (!selected) return;
    setSpecialBusy(true);
    setError("");
    try {
      const saved = special.map((row) => ({ ...row }));
      await apiPost<{ ok: boolean }>("google-special-hours.php", {
        location: selected,
        special_hours: saved,
      });
      patchPlace(selected, { special_hours: saved });
      setNotice("Horaires spéciaux enregistrés.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setSpecialBusy(false);
    }
  }

  async function saveAttrs() {
    if (!selected) return;
    setAttrsBusy(true);
    setError("");
    try {
      await apiPost<{ ok: boolean }>("google-attributes.php", { location: selected, attributes: attrs });
      setNotice("Attributs enregistrés.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setAttrsBusy(false);
    }
  }

  async function addAction(type: string, uri: string) {
    if (!selected) return;
    setActionsBusy(true);
    setError("");
    try {
      await apiPost<{ ok: boolean }>("google-actions.php", { location: selected, type, uri });
      await loadPlaceExtra(selected);
      setNotice("Action ajoutée.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setActionsBusy(false);
    }
  }

  async function deleteAction(name: string) {
    setError("");
    try {
      await apiPost<{ ok: boolean }>("google-actions.php", { delete: name });
      await loadPlaceExtra(selected);
    } catch (err) {
      setError(humanError(err));
    }
  }

  async function addPhotoFile(file: File, category: string) {
    if (!selected) return;
    setPhotoBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("photo", file);
      form.append("location", selected);
      form.append("category", category);
      await apiPostForm<{ ok: boolean }>("google-photos.php", form);
      await loadPlaceExtra(selected);
      setNotice("Photo envoyée.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setPhotoBusy(false);
    }
  }

  async function deletePhoto(name: string) {
    setError("");
    try {
      await apiPost<{ ok: boolean }>("google-photos.php", { delete: name });
      await loadPlaceExtra(selected);
    } catch (err) {
      setError(humanError(err));
    }
  }

  async function createGroup(name: string) {
    setGroupBusy(true);
    setError("");
    try {
      const data = await apiPost<{ group: PlaceGroup; groups: PlaceGroup[] }>("google-groups.php", { name });
      setGroups(data.groups || []);
      setGroupFilter(data.group?.id || "all");
      setNotice("Groupe créé.");
    } catch (err) {
      setError(humanError(err));
    } finally {
      setGroupBusy(false);
    }
  }

  async function assignGroup(location: string, groupId: string) {
    setError("");
    try {
      await apiPost<{ ok: boolean }>("google-group-assign.php", { location, group_id: groupId });
      await loadPlaces();
      setNotice("Fiche déplacée.");
    } catch (err) {
      setError(humanError(err));
    }
  }

  const connected = Boolean(me?.google.connected);
  const visiblePlaces = places.filter((p) => {
    if (groupFilter === "all") return true;
    if (groupFilter === "none") return !p.group_id;
    return p.group_id === groupFilter;
  });

  const titles: Record<Tab, { t: string; s: string }> = {
    places: { t: "Fiches", s: "Un établissement, une section à la fois." },
    reviews: { t: "Avis", s: "Les plus récents, avec vos modèles." },
    photos: { t: "Photos", s: "Couverture, profil et galerie." },
    stats: { t: "Statistiques", s: "Les 28 derniers jours." },
    publish: { t: "Publier", s: "Calendrier Facebook et Instagram." },
  };

  return (
    <div className="pr-shell">
      {me ? <WorkspaceNav tab={tab} onTab={setTab} /> : null}
      <div className="pr-main">
        <Topbar
          google={me?.google ?? { ready: false, connected: false, email: null, connected_at: null, location_count: 0, preview: false }}
          disconnectBusy={busy}
          onDisconnectGoogle={() => void disconnectGoogle()}
        />
        {preview ? (
          <p className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-xs text-amber-900">
            Aperçu. Les changements restent ici jusqu’à l’ouverture de l’accès Google.
          </p>
        ) : null}
        {notice ? <p className="pr-fade mx-5 mt-4 rounded-2xl bg-emerald-50 px-4 py-2 text-sm text-emerald-800">{notice}</p> : null}
        {error ? <p className="pr-fade mx-5 mt-4 rounded-2xl bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p> : null}

        <div className="flex-1 px-5 py-8 lg:px-10">
          {tab !== "publish" ? (
            <GoogleConnect
              status={me?.google ?? { ready: false, connected: false, email: null, connected_at: null, location_count: 0, preview: false }}
              busy={busy}
              onConnect={() => void connectGoogle()}
            />
          ) : null}

          {me && (connected || tab === "publish") ? (
            <div className="pr-fade mx-auto max-w-6xl space-y-6">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-semibold tracking-tight">{titles[tab].t}</h1>
                  <p className="mt-1 text-sm text-slate-500">{titles[tab].s}</p>
                </div>
                {tab !== "places" && tab !== "publish" ? (
                  <PlaceSwitcher
                    places={visiblePlaces}
                    selected={tab === "reviews" && reviewAll ? "all" : selected}
                    allowAll={tab === "reviews" && visiblePlaces.length > 1}
                    wide={tab === "stats"}
                    onSelect={(name) => void pickScope(name)}
                  />
                ) : null}
              </div>

              {tab === "places" ? (
                <div className="space-y-5">
                  <GroupsSidebar
                    groups={groups}
                    places={places}
                    selectedGroup={groupFilter}
                    busy={groupBusy}
                    onSelect={setGroupFilter}
                    onCreate={createGroup}
                  />
                  <PlacesPanel
                    places={visiblePlaces}
                    groups={groups}
                    selected={selected}
                    draft={draft}
                    busy={hoursBusy}
                    onSelect={(name) => void selectPlace(name)}
                    onDraft={setDraft}
                    onSave={() => void saveHours()}
                    onAssign={(location, groupId) => void assignGroup(location, groupId)}
                    special={special}
                    onSpecial={setSpecial}
                    onSaveSpecial={() => void saveSpecial()}
                    specialBusy={specialBusy}
                    onOpen={(status) => void saveOpen(status)}
                    openBusy={openBusy}
                    attrs={attrs}
                    onToggleAttr={(id) => setAttrs((prev) => prev.map((a) => (a.id === id ? { ...a, on: !a.on } : a)))}
                    onSaveAttrs={() => void saveAttrs()}
                    attrsBusy={attrsBusy}
                    actions={actions}
                    actionTypes={actionTypes}
                    onAddAction={addAction}
                    onDeleteAction={(name) => void deleteAction(name)}
                    actionsBusy={actionsBusy}
                  />
                </div>
              ) : null}

              {tab === "reviews" ? (
                <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_440px]">
                  <ReviewsPanel reviews={reviews} templates={templates} busyName={replyBusy} onReply={replyReview} />
                  <ReplyTemplates templates={templates} busy={templatesBusy} onSave={saveTemplate} onDelete={(id) => void deleteTemplate(id)} />
                </div>
              ) : null}

              {tab === "photos" ? (
                <PhotosPanel photos={photos} busy={photoBusy} onAddFile={addPhotoFile} onDelete={(name) => void deletePhoto(name)} />
              ) : null}

              {tab === "stats" ? <StatsPanel stats={stats} /> : null}

              {tab === "publish" ? (
                <PublishPage onNotice={setNotice} onError={(msg) => setError(msg)} />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
